import * as THREE from "three/webgpu";
import {BehaviorSubject, type Observable} from 'rxjs';
import {LineHorizontalConstraint} from '../sketch/solver/constraint/relationDefinition/line/LineHorizontalConstraint';
import {LineVerticalConstraint} from '../sketch/solver/constraint/relationDefinition/line/LineVerticalConstraint';

import {PointCurveCoincidentConstraint} from '../sketch/solver/constraint/relationDefinition/point/PointCurveCoincidentConstraint';


import {PointMidpointConstraint} from '../sketch/solver/constraint/relationDefinition/point/PointMidpointConstraint';

import {SketchRelationKind} from '../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';
import {
    SketchCollectionCreationSource,
    SketchCollectionObject,
    SketchLineObject,
    SketchPointBoxSelectionRole,
    SketchPointKind,
    SketchPointObject,
} from '../mesh/sketch';
import {SketchDrawingHandler, SketchDrawingMouseDownPayload,} from './SketchDrawingHandler';
import type {SketchDrawingManager} from '../manager/SketchDrawingManager';
import {SketchDrawingTool} from './DrawingSharedTypes';
import {addObject3DToParent} from '../utils/scene-object/addObject3DToParent';
import {isDashDotLIneNodeMaterial, LineMaterialFactory, PointsMaterialFactory,} from '../material';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../sketch/config/SketchGeometryToleranceConfig';


/** 矩形预览颜色。 */
const RECTANGLE_PREVIEW_COLOR = 0x53a9ff;


/**
 * 矩形绘制命令枚举。
 */
export enum SketchRectangleCommand {
    /** 角点矩形命令。 */
    Corner = 'sketch-rectangle',
    /** 中心矩形命令。 */
    Center = 'sketch-rectangle-center',
    /** 三点角矩形命令。 */
    ThreePointCorner = 'sketch-rectangle-3-point-corner',
    /** 三点中心矩形命令。 */
    ThreePointCenter = 'sketch-rectangle-3-point-center',
    /** 平行四边形命令。 */
    Parallelogram = 'sketch-rectangle-parallelogram',
}

/**
 * 判断命令是否为矩形绘制命令。
 * @param command 命令字符串。
 * @returns 是否为矩形绘制命令。
 */
export function isSketchRectangleCommand(command: string): command is SketchRectangleCommand {
    return Object.values(SketchRectangleCommand).includes(command as SketchRectangleCommand);
}

/**
 * 矩形构造线模式枚举。
 */
export enum RectangleConstructionLines {
    /** 无构造线。 */
    None = 'none',
    /** 角点构造线。 */
    Corners = 'corners',
    /** 中点构造线。 */
    Midpoints = 'midpoints',
}

/**
 * 矩形绘制选项。
 */
export interface RectangleOptions {
    /** 矩形绘制模式。 */
    mode: SketchRectangleCommand;
    /** 是否为构造几何。 */
    forConstruction: boolean;
    /** 构造线模式。 */
    constructionLines: RectangleConstructionLines;
}


/**
 * 矩形附加构造线来源。
 */
export type RectangleConstructionLineSource =
    RectangleConstructionLines.Corners
    | RectangleConstructionLines.Midpoints;

/**
 * 矩形附加构造线会话缓存项。
 */
export interface RectangleConstructionLinesSessionState {
    /** 是否勾选“添加构造线”。 */
    enabled: boolean;
    /** “从角点/从中点”的来源选择。 */
    source: RectangleConstructionLineSource;
}



/**
 * 创建默认矩形绘制选项。
 *
 * @returns 默认矩形绘制选项。
 */
export function createDefaultRectangleOptions(): RectangleOptions {
    // 普通矩形默认不添加附加构造线，中心类矩形在模式切换时再强制开启。
    return {
        mode: SketchRectangleCommand.Corner,
        forConstruction: false,
        constructionLines: RectangleConstructionLines.None,
    };
}



/**
 * 判断构造线模式是否为可缓存的来源值。
 *
 * @param value 构造线模式。
 * @returns 是“从角点”或“从中点”时返回 true。
 */
export function isRectangleConstructionLineSource(
    value: RectangleConstructionLines,
): value is RectangleConstructionLineSource {
    return value === RectangleConstructionLines.Corners
        || value === RectangleConstructionLines.Midpoints;
}

/**
 * 创建矩形模式的默认构造线会话状态。
 *
 * @param mode 矩形绘制模式。
 * @returns 该模式首次进入时使用的构造线会话状态。
 */
export function createDefaultRectangleConstructionLinesSessionState(
    mode: SketchRectangleCommand,
): RectangleConstructionLinesSessionState {
    const defaultConstructionLines = resolveRectangleModeDefaultConstructionLines(mode);
    if (defaultConstructionLines) {
        return {
            enabled: true,
            source: defaultConstructionLines,
        };
    }

    // 普通矩形首次进入时不勾选构造线，但来源预置为从角点，便于用户首次勾选后有稳定显示。
    return {
        enabled: false,
        source: RectangleConstructionLines.Corners,
    };
}

/**
 * 将会话缓存状态转换为实际绘制选项中的构造线值。
 *
 * @param mode 矩形绘制模式。
 * @param state 构造线会话状态。
 * @returns 写入矩形绘制选项的构造线值。
 */
export function resolveRectangleConstructionLinesFromSessionState(
    mode: SketchRectangleCommand,
    state: RectangleConstructionLinesSessionState,
): RectangleConstructionLines {
    if (isRectangleConstructionLinesToggleLocked(mode)) {
        return state.source;
    }
    return state.enabled ? state.source : RectangleConstructionLines.None;
}



/**
 * 解析矩形模式切换时的默认构造线来源。
 *
 * 设计说明：
 * 1. 中心矩形进入命令时必须默认添加从角点生成的中心构造线；
 * 2. 三点中心矩形进入命令时必须默认添加从中点生成的中心构造线；
 * 3. 其它矩形命令没有强制构造线来源，避免扩大行为影响面。
 *
 * @param mode 矩形绘制模式。
 * @returns 需要按模式重置的构造线来源；无需重置时返回 null。
 */
export function resolveRectangleModeDefaultConstructionLines(
    mode: SketchRectangleCommand,
): RectangleConstructionLineSource | null {
    if (mode === SketchRectangleCommand.Center) {
        return RectangleConstructionLines.Corners;
    }
    if (mode === SketchRectangleCommand.ThreePointCenter) {
        return RectangleConstructionLines.Midpoints;
    }
    return null;
}

/**
 * 判断矩形模式是否锁定“添加构造线”开关。
 *
 * @param mode 矩形绘制模式。
 * @returns 中心矩形或三点中心矩形返回 true。
 */
export function isRectangleConstructionLinesToggleLocked(mode: SketchRectangleCommand): boolean {
    return resolveRectangleModeDefaultConstructionLines(mode) !== null;
}

/**
 * 根据矩形模式切换生成下一份绘制选项。
 *
 * 说明：该函数只处理“切换模式”这一类交互；传入会话状态时按缓存恢复，
 * 未传入会话状态时按矩形类型默认规则初始化。
 *
 * @param options 当前矩形绘制选项。
 * @param mode 目标矩形绘制模式。
 * @returns 应用于绘制器的下一份矩形绘制选项。
 */
export function createRectangleOptionsForModeChange(
    options: RectangleOptions,
    mode: SketchRectangleCommand,
    sessionState: RectangleConstructionLinesSessionState = createDefaultRectangleConstructionLinesSessionState(mode),
): RectangleOptions {
    return {
        ...options,
        mode,
        constructionLines: resolveRectangleConstructionLinesFromSessionState(mode, sessionState),
    };
}

/**
 * 归一化矩形绘制选项，确保被锁定的矩形模式不能关闭附加构造线。
 *
 * @param options 待归一化的矩形绘制选项。
 * @returns 满足中心矩形构造线约束的矩形绘制选项。
 */
export function normalizeRectangleDrawingOptions(options: RectangleOptions): RectangleOptions {
    const defaultConstructionLines = resolveRectangleModeDefaultConstructionLines(options.mode);
    if (
        defaultConstructionLines === null
        || options.constructionLines !== RectangleConstructionLines.None
    ) {
        return {...options};
    }

    // 中心类矩形只锁定“是否添加构造线”，当来源被显式关闭时恢复该模式默认来源。
    return {
        ...options,
        constructionLines: defaultConstructionLines,
    };
}


/**
 * 矩形绘制参数。
 */
export interface RectangleParameters {
    /** 角点坐标数组。 */
    corners: Array<[number, number, number]>;
    /** 中心坐标。 */
    center?: [number, number, number];
    /** 角点2D坐标数组。 */
    corners2d?: Array<[number, number]>;
    /** 中心2D坐标。 */
    center2d?: [number, number];
}


/**
 * 草图矩形绘制处理器。
 * 说明：
 * 1. 承接旧矩形的点击创建流程；
 * 2. 承接旧矩形的鼠标移动预览显示流程；
 * 3. 不依赖外部 context 或 dependencies，直接使用渲染器能力。
 */
export class SketchRectangleDrawingHandler extends SketchDrawingHandler {
    /** 当前处理器负责的工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.Rectangle;
    /** 矩形工具选项。 */
    public rectangleOptions: RectangleOptions = createDefaultRectangleOptions();

    /** 当前页面生命周期内按矩形类型缓存的构造线配置。 */
    private readonly rectangleConstructionLinesSessionCache = new Map<SketchRectangleCommand, RectangleConstructionLinesSessionState>();

    /** 矩形参数。 */
    public rectangleParameters: RectangleParameters | null = null;
    /** 矩形参数变更流（绘制预览时实时派发）。 */
    private readonly rectangleParametersSubject: BehaviorSubject<RectangleParameters | null> = new BehaviorSubject<RectangleParameters | null>(null);


    /**
     * 应用矩形绘制选项。
     *
     * 说明：
     * 1. 同一矩形类型内切换“添加构造线”或来源时，立即写入该类型缓存；
     * 2. 切换矩形类型时，先保存旧类型状态，再恢复新类型在当前页面生命周期内的缓存；
     * 3. 缓存只保存在当前绘制处理器实例中，页面刷新后实例重建即自然重置。
     *
     * @param options 待应用的矩形绘制选项。
     */
    public applyRectangleOptions(options: RectangleOptions): void {
        const isModeChanged = this.rectangleOptions.mode !== options.mode;
        if (isModeChanged) {
            // 切换类型前先保存旧类型状态，避免下次返回该类型时丢失勾选和来源选择。
            this.cacheRectangleConstructionLinesSessionState(this.rectangleOptions);
            const nextOptions = this.createRectangleOptionsWithSessionCache(options);
            this.rectangleOptions = normalizeRectangleDrawingOptions(nextOptions);
            this.cacheRectangleConstructionLinesSessionState(this.rectangleOptions);
            return;
        }

        // 同一类型内的普通更新直接归一化并写缓存，保证复选框和来源单选的状态可恢复。
        this.rectangleOptions = this.normalizeRectangleOptionsWithSessionCache(options);
        this.cacheRectangleConstructionLinesSessionState(this.rectangleOptions);
    }

    /**
     * 读取当前矩形类型缓存中的构造线来源。
     *
     * @returns 当前矩形类型的“从角点/从中点”来源。
     */
    public getCurrentRectangleConstructionLineSource(): RectangleConstructionLineSource {
        return this.resolveRectangleConstructionLinesSessionState(this.rectangleOptions.mode).source;
    }

    /**
     * 通过目标矩形类型的会话缓存创建矩形绘制选项。
     *
     * @param options 待应用的矩形绘制选项。
     * @returns 已恢复目标类型缓存的矩形绘制选项。
     */
    private createRectangleOptionsWithSessionCache(options: RectangleOptions): RectangleOptions {
        return createRectangleOptionsForModeChange(
            options,
            options.mode,
            this.resolveRectangleConstructionLinesSessionState(options.mode),
        );
    }

    /**
     * 使用会话缓存归一化矩形绘制选项。
     *
     * @param options 待归一化的矩形绘制选项。
     * @returns 已应用中心类矩形强制构造线规则的选项。
     */
    private normalizeRectangleOptionsWithSessionCache(options: RectangleOptions): RectangleOptions {
        if (
            isRectangleConstructionLinesToggleLocked(options.mode)
            && options.constructionLines === RectangleConstructionLines.None
        ) {
            // 外部 SDK 直接传入 None 时，也应恢复当前类型缓存来源，而不是固定回到模式初始来源。
            return {
                ...options,
                constructionLines: this.resolveRectangleConstructionLinesSessionState(options.mode).source,
            };
        }
        return normalizeRectangleDrawingOptions(options);
    }

    /**
     * 解析指定矩形类型的构造线缓存状态。
     *
     * @param mode 矩形绘制模式。
     * @returns 当前页面生命周期内该类型缓存的构造线状态。
     */
    private resolveRectangleConstructionLinesSessionState(
        mode: SketchRectangleCommand,
    ): RectangleConstructionLinesSessionState {
        return this.rectangleConstructionLinesSessionCache.get(mode)
            ?? createDefaultRectangleConstructionLinesSessionState(mode);
    }

    /**
     * 缓存矩形类型的构造线状态。
     *
     * @param options 当前矩形绘制选项。
     */
    private cacheRectangleConstructionLinesSessionState(options: RectangleOptions): void {
        const cachedState = this.resolveRectangleConstructionLinesSessionState(options.mode);
        const source = isRectangleConstructionLineSource(options.constructionLines)
            ? options.constructionLines
            : cachedState.source;

        // 当用户取消勾选时，仍保留上一次来源，下一次重新勾选可恢复原先的“从角点/从中点”。
        this.rectangleConstructionLinesSessionCache.set(options.mode, {
            enabled: isRectangleConstructionLinesToggleLocked(options.mode)
                || options.constructionLines !== RectangleConstructionLines.None,
            source,
        });
    }

    /** 当前可编辑的矩形边段集合对象。 */
    private currentRectangleSegmentGeometry: SketchCollectionObject | null = null;
    /** 当前可编辑矩形的四个角点对象。 */
    private currentRectangleCornerPoints: SketchPointObject[] = [];

    /** 当前绘制点对应的捕捉来源点对象。 */
    private drawingPointTargets: Array<SketchPointObject | null> = [];


    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    constructor(sketchDrawingManager: SketchDrawingManager) {
        super(sketchDrawingManager);
    }

    
    getRequiredPointCount(): number {
        return (this.rectangleOptions.mode === SketchRectangleCommand.Corner || this.rectangleOptions.mode === SketchRectangleCommand.Center) ? 2 : 3;
    }

    
    get closable(): boolean {
        return false;
    }

    
    resetState(): void {
        this.drawingPoints = [];
        this.drawingPointTargets = [];
        this.rectangleParameters = null;
        this.rectangleParametersSubject.next(null);
        this.currentRectangleSegmentGeometry = null;
        this.currentRectangleCornerPoints = [];
    }

    /**
     * 监听矩形参数变更（绘制预览时实时派发角点/中心坐标）。
     * @returns 矩形参数只读流。
     */
    public observeRectangleParameters(): Observable<RectangleParameters | null> {
        return this.rectangleParametersSubject.asObservable();
    }

    /**
     * 获取当前可编辑矩形边段集合对象。
     * @returns 当前矩形集合对象；不存在时返回 null。
     */
    public getCurrentRectangleSegmentGeometry(): SketchCollectionObject | null {
        return this.currentRectangleSegmentGeometry;
    }

    /**
     * 获取当前可编辑矩形角点对象快照。
     * @returns 四个角点对象快照。
     */
    public getCurrentRectangleCornerPointsSnapshot(): readonly SketchPointObject[] {
        return [...this.currentRectangleCornerPoints];
    }

    /**
     * 同步当前矩形参数快照。
     * @param parameters 最新矩形参数。
     */
    public syncRectangleParameters(parameters: RectangleParameters | null): void {
        this.rectangleParameters = parameters;
        this.rectangleParametersSubject.next(parameters);
    }

    /**
     * 获取矩形工具绘制提示。
     * @returns 提示文本。
     */
    getDrawingHint(): string {
        const pointCount = this.drawingPoints.length;
        const mode = this.rectangleOptions.mode;
        let baseHint = '点击第一个角，然后点击对角';

        if (mode === SketchRectangleCommand.Corner) {
            baseHint = pointCount === 0 ? '步骤 1/2：点击第一个角点' : '步骤 2/2：点击对角点';
        } else if (mode === SketchRectangleCommand.Center) {
            baseHint = pointCount === 0 ? '步骤 1/2：点击中心点' : '步骤 2/2：点击角点';
        } else if (mode === SketchRectangleCommand.ThreePointCorner) {
            if (pointCount === 0) {
                baseHint = '步骤 1/3：点击第一个角点';
            } else if (pointCount === 1) {
                baseHint = '步骤 2/3：点击第二个角点';
            } else {
                baseHint = '步骤 3/3：点击第三个角点';
            }
        } else if (mode === SketchRectangleCommand.ThreePointCenter) {
            if (pointCount === 0) {
                baseHint = '步骤 1/3：点击中心点';
            } else if (pointCount === 1) {
                baseHint = '步骤 2/3：点击宽度方向点';
            } else {
                baseHint = '步骤 3/3：点击高度方向点';
            }
        } else if (mode === SketchRectangleCommand.Parallelogram) {
            if (pointCount === 0) {
                baseHint = '步骤 1/3：点击第一个角点';
            } else if (pointCount === 1) {
                baseHint = '步骤 2/3：点击第二个角点';
            } else {
                baseHint = '步骤 3/3：点击第三个角点';
            }
        }

        let extraInfo = '';
        if (pointCount > 0 && this.sketchDrawingManager.getPreviewPoint()) {
            const referencePlane = this.referencePlane;
            const previewPoints = [...this.drawingPoints, this.sketchDrawingManager.getPreviewPoint()] as THREE.Vector3[];
            if (mode === SketchRectangleCommand.Corner) {
                const point1Local = referencePlane.worldToLocal(previewPoints[0]);
                const point2Local = referencePlane.worldToLocal(previewPoints[1]);
                const width = Math.abs(point2Local.x - point1Local.x);
                const height = Math.abs(point2Local.y - point1Local.y);
                extraInfo += ` - 宽: ${width.toFixed(2)}, 高: ${height.toFixed(2)}`;
            } else if (mode === SketchRectangleCommand.Center) {
                const centerLocal = referencePlane.worldToLocal(previewPoints[0]);
                const cornerLocal = referencePlane.worldToLocal(previewPoints[1]);
                const width = Math.abs(cornerLocal.x - centerLocal.x) * 2;
                const height = Math.abs(cornerLocal.y - centerLocal.y) * 2;
                extraInfo += ` - 宽: ${width.toFixed(2)}, 高: ${height.toFixed(2)}`;
            } else if (mode === SketchRectangleCommand.ThreePointCorner) {
                if (previewPoints.length >= 2) {
                    const pointA = referencePlane.worldToLocal(previewPoints[0]);
                    const pointB = referencePlane.worldToLocal(previewPoints[1]);
                    const base = Math.hypot(pointB.x - pointA.x, pointB.y - pointA.y);
                    if (previewPoints.length >= 3) {
                        const pointC = referencePlane.worldToLocal(previewPoints[2]);
                        const dx = pointB.x - pointA.x;
                        const dy = pointB.y - pointA.y;
                        const length = Math.hypot(dx, dy) || 1;
                        const px = -dy / length;
                        const py = dx / length;
                        const height = Math.abs((pointC.x - pointA.x) * px + (pointC.y - pointA.y) * py);
                        extraInfo += ` - 宽: ${base.toFixed(2)}, 高: ${height.toFixed(2)}`;
                    } else {
                        extraInfo += ` - 宽: ${base.toFixed(2)}`;
                    }
                }
            } else if (mode === SketchRectangleCommand.ThreePointCenter) {
                const centerLocal = referencePlane.worldToLocal(previewPoints[0]);
                const axisLocal = referencePlane.worldToLocal(previewPoints[1]);
                const halfWidth = Math.hypot(axisLocal.x - centerLocal.x, axisLocal.y - centerLocal.y);
                if (previewPoints.length >= 3) {
                    const heightLocal = referencePlane.worldToLocal(previewPoints[2]);
                    const dx = axisLocal.x - centerLocal.x;
                    const dy = axisLocal.y - centerLocal.y;
                    const length = Math.hypot(dx, dy) || 1;
                    const px = -dy / length;
                    const py = dx / length;
                    const halfHeight = (heightLocal.x - centerLocal.x) * px + (heightLocal.y - centerLocal.y) * py;
                    extraInfo += ` - 宽: ${(halfWidth * 2).toFixed(2)}, 高: ${Math.abs(halfHeight * 2).toFixed(2)}`;
                } else {
                    extraInfo += ` - 宽: ${(halfWidth * 2).toFixed(2)}`;
                }
            } else if (mode === SketchRectangleCommand.Parallelogram && previewPoints.length >= 2) {
                const pointA = referencePlane.worldToLocal(previewPoints[0]);
                const pointB = referencePlane.worldToLocal(previewPoints[1]);
                const width = Math.hypot(pointB.x - pointA.x, pointB.y - pointA.y);
                if (previewPoints.length >= 3) {
                    const pointC = referencePlane.worldToLocal(previewPoints[2]);
                    const height = Math.hypot(pointC.x - pointB.x, pointC.y - pointB.y);
                    extraInfo += ` - 边1: ${width.toFixed(2)}, 边2: ${height.toFixed(2)}`;
                } else {
                    extraInfo += ` - 边1: ${width.toFixed(2)}`;
                }
            }
        }

        return `${baseHint}${extraInfo} | ESC取消`;
    }


    /**
     * 处理矩形绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。

     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload): void {
        const resolvedPosition = this.resolveRectangleDrawingPosition(payload);
        this.drawingPoints.push(resolvedPosition.clone());
        this.drawingPointTargets.push(payload.snappedTargetPoint ?? null);
        this.processRectangleDrawingStep();
    }


    /**
     * 统一预览更新入口。

     * @param currentPoint 当前预览点。
     */
    updatePreview(currentPoint: THREE.Vector3): void {
        const firstPoint = this.drawingPoints[0];
        if (!firstPoint) {
            return;
        }

        if (this.rectangleOptions.mode === SketchRectangleCommand.Corner || this.rectangleOptions.mode === SketchRectangleCommand.Center) {
            this.createRectanglePreviewInHandler([firstPoint, currentPoint]);
            return;
        }

        if (this.drawingPoints.length === 1) {
            this.createLinePreview(firstPoint, currentPoint);
            this.rectangleParameters = null;
            this.rectangleParametersSubject.next(null);
            return;
        }

        const previewPoints = [...this.drawingPoints, currentPoint];
        this.createRectanglePreviewInHandler(previewPoints);
    }


    /**
     * 获取矩形绘制所需点数。
     * @param mode 矩形模式。
     * @returns 所需点数。
     */
    public getRectanglePointCount(mode: RectangleOptions['mode']): number {
        return (mode === SketchRectangleCommand.Corner || mode === SketchRectangleCommand.Center) ? 2 : 3;
    }


    /**
     * 从绘制点计算矩形四角世界/局部坐标。
     * @param points 绘制点列表。
     * @returns 矩形数据，若无效返回null。
     */
    public buildRectangleFromPoints(
        points: THREE.Vector3[]
    ): {
        cornersWorld: THREE.Vector3[];
        centerWorld: THREE.Vector3;
        cornersLocal: THREE.Vector2[];
        centerLocal: THREE.Vector2;
    } | null {
        const mode = this.rectangleOptions.mode;
        const requiredPoints = this.getRectanglePointCount(mode);
        if (points.length < requiredPoints) {
            return null;
        }
        const localPoints = points.slice(0, requiredPoints).map((point) => this.referencePlane.worldToLocal(point));
        const localData = this.buildRectangleLocalCorners(mode, localPoints);
        if (!localData) {
            return null;
        }
        const cornersWorld = localData.corners.map((corner) => this.referencePlane.localToWorld(new THREE.Vector2(corner.x, corner.y)));
        const centerWorld = this.referencePlane.localToWorld(new THREE.Vector2(localData.center.x, localData.center.y));
        return {
            cornersWorld,
            centerWorld,
            cornersLocal: localData.corners,
            centerLocal: localData.center,
        };
    }


    /**
     * 构建矩形对角线/中点辅助构造线。
     * @param cornersWorld 矩形四角世界坐标。
     * @param mode 构造线模式。
     * @param cornerPoints 矩形四角共享控制点。
     * @param isPreview 是否预览模式。
     * @returns 构造线与新创建控制点。
     * @throws 当角点控制点不足或构造线模式不受支持时抛出错误。
     */
    public buildRectangleConstructionLines(
        cornersWorld: THREE.Vector3[],
        mode: RectangleConstructionLines,
        cornerPoints: readonly SketchPointObject[],
        isPreview: boolean = false,
    ): { lines: SketchLineObject[]; createdPoints: SketchPointObject[] } {
        if (mode === RectangleConstructionLines.None || cornersWorld.length < 4) {
            return { lines: [], createdPoints: [] };
        }
        if (cornerPoints.length < 4) {
            throw new Error('矩形角点控制点数量不足，无法创建构造线。');
        }
        const sharedPointsMap = new Map<string, SketchPointObject>();
        const createdPoints: SketchPointObject[] = [];
        cornersWorld.forEach((corner, cornerIndex) => {
            sharedPointsMap.set(this.buildSketchPointShareKey(corner), cornerPoints[cornerIndex]);
        });
        const lines: SketchLineObject[] = [];
        /** 按世界坐标获取可复用控制点。 */
        const resolveSharedPoint = (point: THREE.Vector3): SketchPointObject => {
            const pointKey = this.buildSketchPointShareKey(point);
            const existingPoint = sharedPointsMap.get(pointKey);
            if (existingPoint) {
                return existingPoint;
            }
            const createdPoint = this.createLineControlPoint(point);
            sharedPointsMap.set(pointKey, createdPoint);
            createdPoints.push(createdPoint);
            return createdPoint;
        };
        /** 创建单条构造线。 */
        const createLine = (
            start: THREE.Vector3,
            end: THREE.Vector3,
        ): SketchLineObject => {
            const lineMaterial = isPreview
                ? LineMaterialFactory.getPreviewDashedMaterial().clone()
                : this.createSketchLineMaterial(true, false);
            if (isPreview && lineMaterial instanceof THREE.LineDashedMaterial) {
                lineMaterial.color.setHex(RECTANGLE_PREVIEW_COLOR);
                lineMaterial.opacity = 0.8;
                lineMaterial.transparent = true;
            }
            const line = new SketchLineObject({
                startPoint: resolveSharedPoint(start),
                endPoint: resolveSharedPoint(end),
                material: lineMaterial,
                referencePlane: this.referencePlane,
                isConstruction: true,

                isDrawingPreviewLine: isPreview,
                isAuxiliaryObject: isPreview,

            });
            if (line.material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(line.material)) {
                line.computeLineDistances();
            }
            return line;
        };
        const [c0, c1, c2, c3] = cornersWorld;
        if (mode === RectangleConstructionLines.Corners) {
            lines.push(createLine(c0, c2));
            lines.push(createLine(c1, c3));

            if (!isPreview) {
                createdPoints.push(this.createRectangleConstructionIntersectionPoint(c0, c2));
            }

            return {
                lines,
                createdPoints,
            };
        }
        if (mode === RectangleConstructionLines.Midpoints) {
            const mid01 = new THREE.Vector3().addVectors(c0, c1).multiplyScalar(0.5);
            const mid12 = new THREE.Vector3().addVectors(c1, c2).multiplyScalar(0.5);
            const mid23 = new THREE.Vector3().addVectors(c2, c3).multiplyScalar(0.5);
            const mid30 = new THREE.Vector3().addVectors(c3, c0).multiplyScalar(0.5);
            lines.push(
                createLine(mid01, mid23)
            );
            lines.push(
                createLine(mid12, mid30)
            );

            if (!isPreview) {
                createdPoints.push(this.createRectangleConstructionIntersectionPoint(c0, c2));
            }

            return {
                lines,
                createdPoints,
            };
        }
        throw new Error(`未支持的矩形构造线模式：${mode as string}`);
    }


    /**
     * 创建矩形构造线交点草图点。
     *
     * 说明：
     * 1. 矩形正式创建后，两条构造线交点处生成可独立操作的草图点；
     * 2. 该点只在创建时按当前矩形中心定位一次，不跟随构造线拖动实时重投影；
     * 3. 该点不作为构造线端点，避免改变两条完整构造线的拓扑。
     *
     * @param firstCorner 第一个对角点。
     * @param oppositeCorner 相对对角点。
     * @returns 矩形构造线交点草图点。
     */
    private createRectangleConstructionIntersectionPoint(
        firstCorner: THREE.Vector3,
        oppositeCorner: THREE.Vector3,
    ): SketchPointObject {
        const centerPoint = new THREE.Vector3().addVectors(firstCorner, oppositeCorner).multiplyScalar(0.5);
        return new SketchPointObject({
            position: centerPoint,
            pointKind: SketchPointKind.SketchPoint,
            material: PointsMaterialFactory.getSketchPointSpriteMaterial(),
            referencePlane: this.referencePlane,
            // 保留专用角色以驱动矩形整体拖动，并兼容旧数据中的中心控制点语义。
            boxSelectionRole: SketchPointBoxSelectionRole.RectangleConstructionIntersection,
        });
    }


    /**
     * 构建草图点共享键。
     * @param point 世界坐标点。
     * @returns 共享键。
     */
    private buildSketchPointShareKey(point: THREE.Vector3): string {
        const round = (value: number): string => value.toFixed(8);
        return `${round(point.x)},${round(point.y)},${round(point.z)}`;
    }


    /**
     * 为矩形边段应用默认水平/垂直约束。
     * @param segmentLine 矩形边段线对象。
     * @param segmentIndex 边段索引。
     * @param mode 矩形模式。
     */
    public applyDefaultRectangleSegmentConstraint(
        segmentLine: SketchLineObject,
        segmentIndex: number,
        mode: RectangleOptions['mode']
    ): void {
        if (!this.isAutoGeometryRelationsEnabled()) {
            return;
        }
        if (mode !== SketchRectangleCommand.Corner && mode !== SketchRectangleCommand.Center) {
            return;
        }
        const relationType = segmentIndex % 2 === 0
            ? SketchRelationKind.Horizontal
            : SketchRelationKind.Vertical;
        const created = this.viewer.sketchRelationCreationService.createRelation({
            relationKind: relationType,
            objects: [segmentLine],
            historyMode: 'parent-managed',
        });
        if (!created) {
            throw new Error(`矩形边段自动关系 ${relationType} 创建失败。`);
        }
    }

    /**
     *
     * 为矩形整组边段应用默认几何关系。
     * @param segmentLines 矩形边段集合。
     * @param mode 矩形模式。
     */
    public applyDefaultRectangleRelations(
        segmentLines: readonly SketchLineObject[],
        mode: RectangleOptions['mode'],
    ): void {
        if (!this.isAutoGeometryRelationsEnabled()) {
            return;
        }
        if (segmentLines.length < 4) {
            throw new Error('矩形外框线段数量不足，无法创建默认几何关系。');
        }

        if (mode === SketchRectangleCommand.Corner || mode === SketchRectangleCommand.Center) {
            const created = this.viewer.sketchRelationCreationService.createRelations({
                relations: segmentLines.slice(0, 4).map((segmentLine, segmentIndex) => ({
                    relationKind: segmentIndex % 2 === 0
                        ? SketchRelationKind.Horizontal
                        : SketchRelationKind.Vertical,
                    objects: [segmentLine],
                })),
                historyMode: 'parent-managed',
            });
            if (!created) {
                throw new Error('矩形水平/竖直自动关系创建失败。');
            }
            return;
        }

        if (
            mode === SketchRectangleCommand.ThreePointCorner
            || mode === SketchRectangleCommand.ThreePointCenter
        ) {
            /**
             * 三点矩形以第 0 边作为用户确定角度的基准长边：
             * 1. 第 1、3 边分别垂直于基准边；
             * 2. 第 2 边平行于基准边；
             * 3. 第 1、3 边的平行性由两条垂直关系自然推出，禁止再添加冗余关系。
             */
            const created = this.viewer.sketchRelationCreationService.createRelations({
                relations: [
                    {
                        relationKind: SketchRelationKind.Perpendicular,
                        objects: [segmentLines[0], segmentLines[1]],
                    },
                    {
                        relationKind: SketchRelationKind.Perpendicular,
                        objects: [segmentLines[0], segmentLines[3]],
                    },
                    {
                        relationKind: SketchRelationKind.Parallel,
                        objects: [segmentLines[0], segmentLines[2]],
                    },
                ],
                historyMode: 'parent-managed',
            });
            if (!created) {
                throw new Error('三点矩形垂直/平行自动关系创建失败。');
            }
            return;
        }

        if (mode !== SketchRectangleCommand.Parallelogram) {
            return;
        }

        /**
         * 平行四边形默认创建两组对边平行关系：
         * 1. 第 0 边与第 2 边；
         * 2. 第 1 边与第 3 边。
         */
        const created = this.viewer.sketchRelationCreationService.createRelations({
            relations: [
                {
                    relationKind: SketchRelationKind.Parallel,
                    objects: [segmentLines[0], segmentLines[2]],
                },
                {
                    relationKind: SketchRelationKind.Parallel,
                    objects: [segmentLines[1], segmentLines[3]],
                },
            ],
            historyMode: 'parent-managed',
        });
        if (!created) {
            throw new Error('平行四边形自动平行关系创建失败。');
        }
    }


    /**
     * 执行矩形绘制单步提交。
     * 说明：
     * 1. 当临时点数量达到当前模式所需数量时创建矩形；
     * 2. 创建后清空临时点与预览。
     */
    private processRectangleDrawingStep(): void {
        const requiredPointCount = this.getRequiredPointCount();
        if (this.drawingPoints.length !== requiredPointCount) {
            return;
        }
        this.createRectangleByHandler();

        this.cleanupDrawingState({});
        this.drawingPointTargets = [];
        // 创建完成后结束绘制预览参数流，属性面板随后从已选矩形集合回读可编辑参数。
        this.rectangleParametersSubject.next(null);
    }

    /**
     * 在处理器内创建矩形轮廓预览。

     * @param points 输入点列表。
     */
    private createRectanglePreviewInHandler(points: THREE.Vector3[]): void {
        this.destroyPreview();
        const rectangleData = this.buildRectangleFromPoints(points);
        if (!rectangleData) {
            return;
        }
        const closedPoints = [...rectangleData.cornersWorld, rectangleData.cornersWorld[0]];
        const geometry = new THREE.BufferGeometry().setFromPoints(closedPoints);
        const isConstruction = this.rectangleOptions.forConstruction;
        const material = isConstruction
            ? new THREE.LineDashedMaterial({
                color: RECTANGLE_PREVIEW_COLOR,
                linewidth: 1,
                dashSize: 3,
                gapSize: 2,
                opacity: 0.8,
                transparent: true,
            })
            : new THREE.LineBasicMaterial({
                color: RECTANGLE_PREVIEW_COLOR,
                linewidth: 2,
                opacity: 0.8,
                transparent: true,
            });
        this.sketchDrawingManager.previewRect = new THREE.Line(geometry, material);
        if (material instanceof THREE.LineDashedMaterial) {
            this.sketchDrawingManager.previewRect.computeLineDistances();
        }
        this.viewer.addObject(this.sketchDrawingManager.previewRect);

        this.createRectangleConstructionPreviewInHandler(rectangleData.cornersWorld);


        // 派发预览矩形参数更新事件
        const round = (value: number): number => Number(value.toFixed(8));
        const previewParams: RectangleParameters = {
            corners: rectangleData.cornersWorld.map((c) => c.toArray() as [number, number, number]),
            center: rectangleData.centerWorld.toArray() as [number, number, number],
            corners2d: rectangleData.cornersLocal.map((c) => [round(c.x), round(c.y)]),
            center2d: [round(rectangleData.centerLocal.x), round(rectangleData.centerLocal.y)],
        };
        this.rectangleParameters = previewParams;
        this.rectangleParametersSubject.next(previewParams);
    }


    /**
     * 在处理器内创建矩形构造线预览。
     *
     * @param cornersWorld 矩形四个角点（世界坐标）。
     */
    private createRectangleConstructionPreviewInHandler(cornersWorld: THREE.Vector3[]): void {
        if (this.rectangleOptions.constructionLines === RectangleConstructionLines.None) {
            return;
        }
        const previewCornerPoints = cornersWorld.map((corner) => this.createLineControlPoint(corner, true));
        const {lines: constructionLines} = this.buildRectangleConstructionLines(
            cornersWorld,
            this.rectangleOptions.constructionLines,
            previewCornerPoints,
            true,
        );
        this.sketchDrawingManager.previewConstructionLines = constructionLines;
        constructionLines.forEach((constructionLine) => {
            this.viewer.addObject(constructionLine);
        });
    }


    /**
     * 在处理器内创建矩形边段几何对象。
     * 说明：
     * 1. 保持原有线段、控制点创建行为；
     * 2. 不再通过 Viewer 中转，直接在矩形处理器内构建。
     * @param cornersWorld 矩形四个角点（世界坐标）。
     * @param cornerSourcePoints 四个角点对应的捕捉来源点。
     * @returns 矩形边段几何对象、角点控制点与边段线集合。
     */
    private createRectangleSegmentGeometryObjectInHandler(
        cornersWorld: THREE.Vector3[],
        cornerSourcePoints: ReadonlyArray<SketchPointObject | null> = [],
    ): { geometryObject: SketchCollectionObject; cornerPoints: SketchPointObject[]; segmentLines: SketchLineObject[] } {
        const referencePlane = this.referencePlane;
        const points = cornersWorld.map((corner, index) => this.createLineControlPoint(
            corner,
            false,
            cornerSourcePoints[index] ?? null,
        ));
        const lines: SketchLineObject[] = [];
        for (let index = 0; index < points.length; index += 1) {
            const nextIndex = (index + 1) % points.length;
            const lineMaterial = this.createSketchLineMaterial(false);
            const line = new SketchLineObject({
                startPoint: points[index],
                endPoint: points[nextIndex],
                material: lineMaterial,
                referencePlane,
            });
            if (line.material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(line.material)) {
                line.computeLineDistances();
            }

            lines.push(line);
        }
        const geometryObject = new SketchCollectionObject({
            sources: lines,
            referencePlane,

            creationSource: SketchCollectionCreationSource.Rectangle,

        });
        if (this.viewer.shadedSketchContoursEnabled) {
            geometryObject.syncContour();
        }

        points.forEach((point) => {
            if (point.parent !== geometryObject) {
            addObject3DToParent(point, geometryObject);
            }
        });


        /**
         * 同步线及控制点的原始材质快照，
         * 避免交互状态切换后材质回退到错误样式。
         */
        lines.forEach((line) => {
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line);
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line.startPoint);
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line.endPoint);
        });


        return {
            geometryObject,
            cornerPoints: points,
            segmentLines: lines,
        };
    }


    /**
     * 在处理器内创建矩形实体并写回参数。
     */
    private createRectangleByHandler(): void {
        const rectangleData = this.buildRectangleFromPoints(this.drawingPoints);
        if (!rectangleData) {
            return;
        }

        const { cornersWorld, centerWorld, cornersLocal, centerLocal} = rectangleData;
        const cornerSourcePoints = this.resolveRectangleCornerSourcePoints(cornersWorld);
        const {geometryObject: rectangleSegmentGeometry, cornerPoints, segmentLines} = this.createRectangleSegmentGeometryObjectInHandler(
            cornersWorld,
            cornerSourcePoints,
        );
        /**
         * 矩形集合必须在创建时保存自己的语义参数。
         * 后续 PropertyManager 选中矩形时会直接读取集合对象，而不是回推当前绘制命令状态。
         */
        rectangleSegmentGeometry.geometryData = {
            ...(rectangleSegmentGeometry.geometryData ?? {}),
            rectangle: {...this.rectangleOptions},
            constructionLines: {
                lineIds: [],

                pointIds: [],

            },
        };
        // 先把矩形集合接入活动草图，再通过唯一关系服务执行整批关系求解与失败回滚。
        this.viewer.addSketchObject(rectangleSegmentGeometry);
        this.applyDefaultRectangleRelations(
            segmentLines,
            this.rectangleOptions.mode,
        );
        this.viewer.rectangleSegmentGeometryMap.set(rectangleSegmentGeometry.uuid, rectangleSegmentGeometry);
        this.sketchDrawingManager.notifySketchEntityCreated(rectangleSegmentGeometry);
        this.currentRectangleSegmentGeometry = rectangleSegmentGeometry;
        this.currentRectangleCornerPoints = [...cornerPoints];
        const sketchGroupManager = this.viewer.sketchGroupManager;
        if (!sketchGroupManager) {
            throw new Error('草图组管理器未初始化，无法同步草图轮廓显示状态。');
        }
        this.viewer.syncShadedContoursAfterSketchEntityCreated(sketchGroupManager.activeSketchGroup);

        if (this.rectangleOptions.constructionLines !== RectangleConstructionLines.None) {
            const { lines: constructionLines, createdPoints: constructionPoints } = this.buildRectangleConstructionLines(
                cornersWorld,
                this.rectangleOptions.constructionLines,
                cornerPoints,
                false,
            );

            const shouldApplyMidpointAutoRelations = this.rectangleOptions.constructionLines === RectangleConstructionLines.Midpoints
                && this.isAutoGeometryRelationsEnabled();
            if (shouldApplyMidpointAutoRelations) {
                this.applyMidpointConstructionLineAutoRelations(segmentLines, constructionLines);
            }


            this.applyConstructionLineCenterPointCoincidentAutoRelations(
                constructionLines,
                constructionPoints,
                centerWorld,
            );

            constructionLines.forEach((constructionLine) => {
                this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(constructionLine);
                this.viewer.addSketchObject(constructionLine);
                this.sketchDrawingManager.notifySketchEntityCreated(constructionLine);
                const geometryData = rectangleSegmentGeometry.geometryData;
                if (geometryData?.constructionLines?.lineIds) {
                    geometryData.constructionLines.lineIds.push(constructionLine.uuid);
                }
            });

            constructionPoints.forEach((constructionPoint) => {
                if (constructionPoint.parent) {
                    return;
                }
                this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(constructionPoint);
                this.viewer.addSketchObject(constructionPoint);

                // 记录构造线中心点归属，移动矩形集合时才能按集合辅助点统一平移。
                const geometryData = rectangleSegmentGeometry.geometryData;
                if (geometryData?.constructionLines?.pointIds) {
                    geometryData.constructionLines.pointIds.push(constructionPoint.uuid);
                }

            });

        }

        const round = (value: number): number => Number(value.toFixed(8));
        this.syncRectangleParameters({
            corners: cornersWorld.map((corner) => corner.toArray() as [number, number, number]),
            center: centerWorld.toArray() as [number, number, number],
            corners2d: cornersLocal.map((corner) => [round(corner.x), round(corner.y)]),
            center2d: [round(centerLocal.x), round(centerLocal.y)],
        });

        /**
         * 对齐 SolidWorks：矩形创建完成后保持选中态，参数面板才能立即编辑。
         * 这里选择矩形集合本身，视觉状态机会递归高亮四条边与角点。
         */
        this.viewer.entitySelectionManager?.selectEntityProgrammatically(rectangleSegmentGeometry);
    }


    /**
     * 为矩形构造线中心草图点绑定重合关系。
     *
     * 规则：
     * 1. 仅在自动几何关系开启时创建；
     * 2. 中心草图点只在矩形创建完成时按当前构造线交点初始化位置；
     * 3. 两条构造线分别与同一个中心草图点建立 `Coincident`，后续由约束系统表达“点在线上”的设计意图。
     *
     * @param constructionLines 矩形构造线集合。
     * @param constructionPoints 构造线额外创建的草图点集合。
     * @param centerWorld 矩形中心世界坐标。
     * @throws 当构造线不足或无法解析中心草图点时抛出错误。
     */
    public applyConstructionLineCenterPointCoincidentAutoRelations(
        constructionLines: readonly SketchLineObject[],
        constructionPoints: readonly SketchPointObject[],
        centerWorld: THREE.Vector3,
    ): void {
        if (!this.isAutoGeometryRelationsEnabled()) {
            return;
        }
        if (constructionLines.length < 2) {
            throw new Error('矩形构造线数量不足，无法创建中心点重合关系。');
        }

        const centerPoint = this.resolveConstructionCenterPoint(constructionPoints, centerWorld);
        if (!centerPoint) {
            throw new Error('矩形构造线中心草图点缺失，无法创建中心点重合关系。');
        }

        constructionLines.slice(0, 2).forEach((constructionLine) => {
            this.bindPointCurveCoincidentConstraint(centerPoint, constructionLine);
        });
    }

    /**
     * 解析构造线中心草图点。
     * @param constructionPoints 构造线额外创建的草图点集合。
     * @param centerWorld 矩形中心世界坐标。
     * @returns 中心草图点；找不到时返回 null。
     */
    private resolveConstructionCenterPoint(
        constructionPoints: readonly SketchPointObject[],
        centerWorld: THREE.Vector3,
    ): SketchPointObject | null {
        return constructionPoints.find((constructionPoint) => {
            return constructionPoint.getPosition().distanceTo(centerWorld)
                <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.rectangleProjectionPosition;
        }) ?? null;
    }

    /**
     * 绑定“点 + 曲线”重合约束。
     * @param point 中心草图点。
     * @param line 目标构造线。
     */
    private bindPointCurveCoincidentConstraint(point: SketchPointObject, line: SketchLineObject): void {
        const coincidentConstraint = new PointCurveCoincidentConstraint();
        point.constraints.addRelation(
            coincidentConstraint as unknown as Parameters<typeof point.constraints.addRelation>[0],
        );
        line.constraints.addRelation(
            coincidentConstraint as unknown as Parameters<typeof line.constraints.addRelation>[0],
        );
    }


    /**
     * 为中点构造线挂载默认自动几何关系。
     * 规则：
     * 1. 4 个构造点分别与外框 4 条边建立中点关系；
     * 2. 竖向中线挂载竖直关系；
     * 3. 横向中线挂载水平关系。
     * @param rectangleSegmentLines 矩形外框线段（按 0~3 顺序）。
     * @param constructionLines 中点构造线（按“竖向、横向”顺序）。
     * @throws 当矩形外框线段或中点构造线数量不足时抛出错误。
     */
    public applyMidpointConstructionLineAutoRelations(
        rectangleSegmentLines: readonly SketchLineObject[],
        constructionLines: readonly SketchLineObject[],
    ): void {
        if (!this.isAutoGeometryRelationsEnabled()) {
            return;
        }
        if (rectangleSegmentLines.length < 4) {
            throw new Error('矩形外框线段数量不足，无法创建中点自动几何关系。');
        }
        if (constructionLines.length < 2) {
            throw new Error('中点构造线数量不足，无法创建中点自动几何关系。');
        }

        /**
         * 中点构造线生成顺序固定为：
         * 1. line[0] = mid01 -> mid23（竖向）；
         * 2. line[1] = mid12 -> mid30（横向）。
         */
        const midpointPointAndOuterLinePairs: Array<{ midpointPoint: SketchPointObject; outerLine: SketchLineObject }> = [
            { midpointPoint: constructionLines[0].startPoint, outerLine: rectangleSegmentLines[0] },
            { midpointPoint: constructionLines[1].startPoint, outerLine: rectangleSegmentLines[1] },
            { midpointPoint: constructionLines[0].endPoint, outerLine: rectangleSegmentLines[2] },
            { midpointPoint: constructionLines[1].endPoint, outerLine: rectangleSegmentLines[3] },
        ];

        midpointPointAndOuterLinePairs.forEach(({ midpointPoint, outerLine }) => {
            this.bindMidpointConstraint(midpointPoint, outerLine);
        });

        // 竖向中线默认挂载竖直关系，横向中线默认挂载水平关系。
        constructionLines[0].constraints.addRelation(new LineVerticalConstraint());
        constructionLines[1].constraints.addRelation(new LineHorizontalConstraint());
    }

    /**
     * 绑定“点+线”中点约束。
     * @param point 中点控制点。
     * @param line 目标外框线段。
     */
    private bindMidpointConstraint(point: SketchPointObject, line: SketchLineObject): void {
        if (!this.isAutoGeometryRelationsEnabled()) {
            return;
        }

        const midpointConstraint = new PointMidpointConstraint();

        point.constraints.addRelation(
            midpointConstraint as unknown as Parameters<typeof point.constraints.addRelation>[0],
        );
        line.constraints.addRelation(
            midpointConstraint as unknown as Parameters<typeof line.constraints.addRelation>[0],
        );
    }


    /**
     * 解析矩形绘制坐标。
     * @param payload 鼠标按下载荷。

     * @returns 解析结果；失败返回 null。
     */
    private resolveRectangleDrawingPosition(payload: SketchDrawingMouseDownPayload): THREE.Vector3 {
        return payload.snappedPoint ?? this.getDrawingPoint(payload.event);
    }


    /**
     * 解析矩形四个最终角点对应的捕捉来源点。
     * 说明：
     * 1. 矩形不同模式下，点击点不一定都是最终角点；
     * 2. 只有点击点与最终角点语义一致，并且位置仍在闭合容差内时才记录来源；
     * 3. 中心矩形与三点中心矩形的中心/方向点不是角点，不写入角点来源。
     * @param cornersWorld 已解算出的矩形四角世界坐标。
     * @returns 与四角索引对应的来源点数组。
     */
    private resolveRectangleCornerSourcePoints(
        cornersWorld: readonly THREE.Vector3[],
    ): Array<SketchPointObject | null> {
        const sourcePoints: Array<SketchPointObject | null> = Array.from(
            {length: cornersWorld.length},
            () => null,
        );
        const mode = this.rectangleOptions.mode;

        /**
         * 每个候选都必须用最终角点坐标复核。
         * 这样三点角矩形第三点被投影修正时，不会把非角点误记录成来源。
         */
        const assignIfMatched = (drawingPointIndex: number, cornerIndex: number): void => {
            sourcePoints[cornerIndex] = this.resolveControlPointSource(
                this.drawingPointTargets[drawingPointIndex] ?? null,
                cornersWorld[cornerIndex] ?? null,
            );
        };

        if (mode === SketchRectangleCommand.Corner) {
            assignIfMatched(0, 0);
            assignIfMatched(1, 2);
            return sourcePoints;
        }

        if (mode === SketchRectangleCommand.Center) {
            assignIfMatched(1, 2);
            return sourcePoints;
        }

        if (mode === SketchRectangleCommand.ThreePointCorner) {
            assignIfMatched(0, 0);
            assignIfMatched(1, 1);
            assignIfMatched(2, 2);
            return sourcePoints;
        }

        if (mode === SketchRectangleCommand.Parallelogram) {
            assignIfMatched(0, 0);
            assignIfMatched(1, 1);
            assignIfMatched(2, 2);
        }

        return sourcePoints;
    }


    /**
     * 根据模式和局部点构建矩形四角局部坐标。
     * @param mode 矩形模式。
     * @param points 局部坐标点列表。
     * @returns 四角和中心局部坐标，若无效返回null。
     */
    private buildRectangleLocalCorners(
        mode: RectangleOptions['mode'],
        points: THREE.Vector2[]
    ): { corners: THREE.Vector2[]; center: THREE.Vector2 } | null {
        const rectangleProjectionPosition = SKETCH_GEOMETRY_TOLERANCE_CONFIG.rectangleProjectionPosition;
        if (mode === SketchRectangleCommand.Corner || mode === SketchRectangleCommand.Center) {
            if (points.length < 2) {
                return null;
            }
            const p0 = points[0];
            const p1 = points[1];
            if (mode === SketchRectangleCommand.Corner) {
                const corners = [
                    new THREE.Vector2(p0.x, p0.y),
                    new THREE.Vector2(p1.x, p0.y),
                    new THREE.Vector2(p1.x, p1.y),
                    new THREE.Vector2(p0.x, p1.y),
                ];
                const center = new THREE.Vector2(
                    (corners[0].x + corners[2].x) / 2,
                    (corners[0].y + corners[2].y) / 2,
                );
                return {corners, center};
            }
            const center = new THREE.Vector2(p0.x, p0.y);
            const halfWidth = p1.x - p0.x;
            const halfHeight = p1.y - p0.y;
            const corners = [
                new THREE.Vector2(center.x - halfWidth, center.y - halfHeight),
                new THREE.Vector2(center.x + halfWidth, center.y - halfHeight),
                new THREE.Vector2(center.x + halfWidth, center.y + halfHeight),
                new THREE.Vector2(center.x - halfWidth, center.y + halfHeight),
            ];
            return {corners, center};
        }

        if (points.length < 3) {
            return null;
        }

        if (mode === SketchRectangleCommand.ThreePointCorner) {
            const a = points[0];
            const b = points[1];
            const c = points[2];
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const length = Math.hypot(dx, dy);
            if (length < rectangleProjectionPosition) {
                return null;
            }
            const ux = dx / length;
            const uy = dy / length;
            const px = -uy;
            const py = ux;
            const vcx = c.x - a.x;
            const vcy = c.y - a.y;
            const height = vcx * px + vcy * py;
            if (Math.abs(height) < rectangleProjectionPosition) {
                return null;
            }
            const p2 = new THREE.Vector2(b.x + px * height, b.y + py * height);
            const p3 = new THREE.Vector2(a.x + px * height, a.y + py * height);
            const corners = [a, b, p2, p3];
            const center = new THREE.Vector2(
                (a.x + p2.x) / 2,
                (a.y + p2.y) / 2,
            );
            return {corners, center};
        }

        if (mode === SketchRectangleCommand.ThreePointCenter) {
            const center = points[0];
            const axisPoint = points[1];
            const heightPoint = points[2];
            const dx = axisPoint.x - center.x;
            const dy = axisPoint.y - center.y;
            const halfWidth = Math.hypot(dx, dy);
            if (halfWidth < rectangleProjectionPosition) {
                return null;
            }
            const ux = dx / halfWidth;
            const uy = dy / halfWidth;
            const px = -uy;
            const py = ux;
            const vhx = heightPoint.x - center.x;
            const vhy = heightPoint.y - center.y;
            const halfHeight = vhx * px + vhy * py;
            if (Math.abs(halfHeight) < rectangleProjectionPosition) {
                return null;
            }
            const corners = [
                new THREE.Vector2(center.x - ux * halfWidth - px * halfHeight, center.y - uy * halfWidth - py * halfHeight),
                new THREE.Vector2(center.x + ux * halfWidth - px * halfHeight, center.y + uy * halfWidth - py * halfHeight),
                new THREE.Vector2(center.x + ux * halfWidth + px * halfHeight, center.y + uy * halfWidth + py * halfHeight),
                new THREE.Vector2(center.x - ux * halfWidth + px * halfHeight, center.y - uy * halfWidth + py * halfHeight),
            ];
            return {corners, center};
        }

        if (mode === SketchRectangleCommand.Parallelogram) {
            const a = points[0];
            const b = points[1];
            const c = points[2];
            const v2x = c.x - b.x;
            const v2y = c.y - b.y;
            if (Math.hypot(v2x, v2y) < rectangleProjectionPosition) {
                return null;
            }
            const d = new THREE.Vector2(a.x + v2x, a.y + v2y);
            const corners = [a, b, c, d];
            const center = new THREE.Vector2(
                (a.x + c.x) / 2,
                (a.y + c.y) / 2,
            );
            return {corners, center};
        }

        return null;
    }

}


